<div class="adoption-page">
  <div class="navigation-breadcrumb">
    <a routerLink="/ops-vendor-management" class="breadcrumb-text">Vendor Management</a>
    <span class="breadcrumb-separator"> / </span>
    <span class="breadcrumb-text non-link">Adoption</span>
  </div>
  <div class="page-header">
    <div class="title-group">
      <emerald-icon icon="insights" color="#003F2D" type="outlined"></emerald-icon>
      <h1>Adoption</h1>
      <span class="subtitle">portal usage &amp; feature adoption</span>
    </div>
    <emerald-dropdown
      [options]="rangeOptions"
      [outlined]="true"
      [id]="'adoption_range'"
      [label]="'Range'"
      (onChange)="onRangeChange($event)"
    ></emerald-dropdown>
  </div>

  <div class="error" *ngIf="loadError">Unable to load adoption data.</div>

  <ng-container *ngIf="!loadError">
    <!-- KPI tiles with icons + descriptions -->
    <div class="kpi-row">
      <div class="kpi" data-tone="users">
        <div class="kpi-head">
          <emerald-icon icon="group" type="outlined" color="#2563eb"></emerald-icon>
          <span class="kpi-label">Unique Users</span>
        </div>
        <div class="kpi-value">{{ loading ? '—' : (summary?.uniqueUsers ?? 0) }}</div>
        <div class="kpi-hint">Distinct people who used the portal in the selected window.</div>
      </div>

      <div class="kpi" data-tone="dau">
        <div class="kpi-head">
          <emerald-icon icon="today" type="outlined" color="#d97706"></emerald-icon>
          <span class="kpi-label">Daily Active</span>
        </div>
        <div class="kpi-value">{{ loading ? '—' : (summary?.dau ?? 0) }}</div>
        <div class="kpi-hint">Distinct users active in the last 24 hours.</div>
      </div>

      <div class="kpi" data-tone="mau">
        <div class="kpi-head">
          <emerald-icon icon="date_range" type="outlined" color="#7c3aed"></emerald-icon>
          <span class="kpi-label">Monthly Active</span>
        </div>
        <div class="kpi-value">{{ loading ? '—' : (summary?.mau ?? 0) }}</div>
        <div class="kpi-hint">Distinct users active in the last 30 days.</div>
      </div>

      <div class="kpi" data-tone="events">
        <div class="kpi-head">
          <emerald-icon icon="touch_app" type="outlined" color="#003F2D"></emerald-icon>
          <span class="kpi-label">Total Events</span>
        </div>
        <div class="kpi-value">{{ loading ? '—' : (summary?.totalEvents ?? 0) }}</div>
        <div class="kpi-hint">Sum of all captured API requests in the window.</div>
      </div>

      <div class="kpi" data-tone="features">
        <div class="kpi-head">
          <emerald-icon icon="category" type="outlined" color="#0891b2"></emerald-icon>
          <span class="kpi-label">Distinct Features</span>
        </div>
        <div class="kpi-value">{{ loading ? '—' : (summary?.distinctFeatures ?? 0) }}</div>
        <div class="kpi-hint">Unique product features touched in the window.</div>
      </div>
    </div>

    <!-- Trend (full width) -->
    <section class="card trend-card">
      <div class="card-header">
        <emerald-icon icon="trending_up" type="outlined" color="#003F2D"></emerald-icon>
        <h2 class="card-title">Active Users &amp; Events Over Time</h2>
      </div>
      <div class="chart-wrapper trend-chart">
        <p-chart *ngIf="trendChartData?.labels?.length" type="line" [data]="trendChartData" [options]="trendChartOptions"></p-chart>
        <div class="empty" *ngIf="!trendChartData?.labels?.length">No data in selected range.</div>
      </div>
    </section>

    <!-- Two-column: features + personas -->
    <div class="grid-two">
      <section class="card">
        <div class="card-header">
          <emerald-icon icon="leaderboard" type="outlined" color="#003F2D"></emerald-icon>
          <h2 class="card-title">Top Features</h2>
          <span class="card-subtitle">most-used product areas</span>
        </div>
        <div class="chart-wrapper bar-chart">
          <p-chart *ngIf="featureChartData?.labels?.length" type="bar" [data]="featureChartData" [options]="featureChartOptions"></p-chart>
          <div class="empty" *ngIf="!featureChartData?.labels?.length">No feature data.</div>
        </div>
      </section>

      <section class="card">
        <div class="card-header">
          <emerald-icon icon="donut_large" type="outlined" color="#003F2D"></emerald-icon>
          <h2 class="card-title">Events by Persona</h2>
          <span class="card-subtitle">who's using the portal</span>
        </div>
        <div class="chart-wrapper donut-chart">
          <p-chart *ngIf="personaChartData?.labels?.length" type="doughnut" [data]="personaChartData" [options]="personaChartOptions"></p-chart>
          <div class="empty" *ngIf="!personaChartData?.labels?.length">No persona data.</div>
        </div>
      </section>
    </div>

    <!-- Active users table -->
    <section class="card users-card">
      <div class="card-header">
        <emerald-icon icon="person_pin" type="outlined" color="#003F2D"></emerald-icon>
        <h2 class="card-title">Most Active Users</h2>
        <span class="card-subtitle">top contributors by event count</span>
      </div>
      <table class="users-table" *ngIf="activeUsers.length; else noUsers">
        <thead>
          <tr>
            <th>Email</th>
            <th>Persona</th>
            <th class="num">Events</th>
            <th>Top Feature</th>
            <th>Last Seen</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let u of activeUsers">
            <td>{{ u.email || u.employeeId || '—' }}</td>
            <td>
              <span class="persona-chip" *ngIf="u.persona; else dashPersona">{{ u.persona }}</span>
              <ng-template #dashPersona>—</ng-template>
            </td>
            <td class="num"><strong>{{ u.eventCount }}</strong></td>
            <td><span class="feature-chip" *ngIf="u.topFeature">{{ u.topFeature }}</span></td>
            <td class="time">{{ u.lastSeen | date:'short' }}</td>
          </tr>
        </tbody>
      </table>
      <ng-template #noUsers>
        <div class="empty">No active users in range.</div>
      </ng-template>
      <div class="show-more-row" *ngIf="activeUsersHasMore && activeUsers.length">
        <button type="button" class="show-more-btn" [disabled]="activeUsersLoadingMore" (click)="showMoreUsers()">
          {{ activeUsersLoadingMore ? 'Loading…' : 'Show more' }}
        </button>
        <span class="show-more-hint">showing top {{ activeUsers.length }}</span>
      </div>
    </section>
  </ng-container>
</div>
